import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { zodResolver } from '@hookform/resolvers/zod'
import { Link, useNavigate } from '@tanstack/react-router'
import { FileText, MapPinned, QrCode, ScanFace, User } from 'lucide-react'
import { GrowerGeneralSection } from './grower-general-section'
import { GrowerAreaQRTrigger } from './grower-area-qr-trigger'
import { GrowerQRDialog } from './grower-qr-dialog'
import { GrowerProfileSection } from './grower-profile-section'
import { GrowerAreasSection } from './grower-areas-section'
import { useAttachmentDraft } from './attachments/use-attachment-draft'
import { useAreaLandDrafts } from './attachments/use-area-land-drafts'
import { NO_USER, growerToFormValues } from './grower-form-values'
import type { AreaDraftTarget } from './attachments/use-area-land-drafts'
import type { Grower, UpsertGrowerRequest } from '@/lib/api/growers-api'
import type { AreaGeo } from './area-map/use-area-polygon'
import type { GrowerFormValues } from '@/lib/schemas/grower-schemas'
import { PageBackButton } from '@/components/common/PageBackButton'
import { Button } from '@/components/ui/button'
import { FormSection } from '@/components/forms/form-section'
import { useAuth } from '@/hooks/use-auth'
import {
    useCreateGrower,
    useGrower,
    useUpdateGrower,
} from '@/hooks/use-growers-query'
import { createGrowerSchema } from '@/lib/schemas/grower-schemas'
import { GoogleMapsProvider } from '@/components/maps/google-maps-provider'
import { uploadFileErrorMessage } from '@/lib/api/files-api'
import { m } from '@/lib/paraglide/messages'

type Props = {
    mode: 'create' | 'edit'
    growerId?: string
    // id có thể undefined nếu response tạo bất thường không có data → route
    // xử lý bằng cách điều hướng về danh sách.
    onSaved: (id?: string) => void
}

/** Trang tạo/sửa hộ trồng: gộp 2 section, submit 1 lần, chặn ghi theo quyền. */
export function GrowerEditPage({ mode, growerId, onSaved }: Props) {
    const navigate = useNavigate()
    const { hasPermission } = useAuth()
    const canWrite = hasPermission('growers.write')
    const readOnly = mode === 'edit' && !canWrite
    const [qrOpen, setQrOpen] = useState(false)
    const [qrPhone, setQrPhone] = useState('')

    const { data: initial } = useGrower(mode === 'edit' ? growerId : undefined)
    const createMut = useCreateGrower()
    const updateMut = useUpdateGrower(growerId ?? '')
    // Giấy tờ đính kèm dạng nháp — chỉ thực sự upload/xoá khi commit sau lưu.
    // Giấy tờ hồ sơ (CCCD…) thuộc về hộ; giấy tờ đất thuộc TỪNG vùng trồng nên
    // có kho nháp riêng, khoá theo vùng.
    const attachments = useAttachmentDraft(
        mode === 'edit' ? growerId : undefined,
        'profile',
    )
    const landDrafts = useAreaLandDrafts(mode === 'edit' ? growerId : undefined)

    const form = useForm<GrowerFormValues>({
        resolver: zodResolver(createGrowerSchema()),
        values: growerToFormValues(initial),
    })

    async function onSubmit(values: GrowerFormValues) {
        const payload: UpsertGrowerRequest = {
            name: values.name,
            nickname: values.nickname || null,
            birthday: values.birthday || null,
            phone: values.phone || null,
            address: values.address || null,
            national_id: values.national_id || null,
            national_id_issued_date: values.national_id_issued_date || null,
            national_id_issued_place: values.national_id_issued_place || null,
            bank_number: values.bank_number || null,
            bank_name: values.bank_name || null,
            bank_account: values.bank_account || null,
            areas: values.areas.map((a) => ({
                id: a.id,
                code: a.code,
                name: a.name,
                acreage: Number(a.acreage) || 0,
                note: a.note,
                paths: a.paths,
                markers: a.markers,
            })),
            user_id:
                values.user_id && values.user_id !== NO_USER
                    ? values.user_id
                    : null,
        }
        try {
            if (mode === 'edit' && growerId) {
                const res = await updateMut.mutateAsync(payload)
                await commitAttachments(growerId, areaTargets(values, res.data))
                onSaved(growerId)
            } else {
                const res = await createMut.mutateAsync(payload)
                const newId = res.data?.id
                if (newId) {
                    await commitAttachments(
                        newId,
                        areaTargets(values, res.data),
                    )
                }
                onSaved(newId)
            }
        } catch {
            // Lỗi lưu hộ đã có toast từ mutation hook; lỗi đính kèm tệp do
            // commitAttachments tự báo.
        }
    }

    /**
     * Đẩy giấy tờ của cả 2 ô lên sau khi hộ đã lưu.
     *
     * Nuốt lỗi ở đây là hộ lưu xong mà tệp biến mất không dấu vết (ảnh bìa đỏ
     * chụp bằng điện thoại vượt giới hạn dung lượng là chuyện thường), nên phải
     * báo NGUYÊN NHÂN THẬT lấy từ lỗi trả về — "Đính kèm giấy tờ thất bại"
     * chung chung thì người dùng không biết nên nén ảnh, xin quyền hay đăng
     * nhập lại. `allSettled` để một ô hỏng không chặn ô còn lại; hai ô cùng
     * dính một lý do thì chỉ báo một lần.
     */
    async function commitAttachments(id: string, targets: AreaDraftTarget[]) {
        const results = await Promise.allSettled([
            attachments.commit(id),
            landDrafts.commitAll(id, targets),
        ])
        const reasons = new Set(
            results
                .filter((r) => r.status === 'rejected')
                .map((r) => uploadFileErrorMessage(r.reason)),
        )
        for (const reason of reasons) {
            toast.error(reason)
        }
        // Vùng không tra ra id sau khi lưu là chuyện không nên xảy ra, nhưng im
        // lặng ở đây thì người dùng thấy giấy tờ đất biến mất mà không hiểu vì
        // sao — nói thẳng để họ vào lại vùng đó tải lên.
        const land = results[1]
        if (land.status === 'fulfilled' && land.value.missingArea > 0) {
            toast.error(m.grower_land_documents_area_missing())
        }
    }

    const submitting = createMut.isPending || updateMut.isPending

    return (
        <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="mx-auto max-w-[1180px] space-y-5"
        >
            {/* Trên điện thoại xếp thành HAI hàng: tiêu đề trên, nút dưới.
                Nhồi cả hai vào một hàng thì bốn nút ăn hết bề ngang và tiêu đề
                bị bóp còn một hai chữ mỗi dòng, cao lêu nghêu ba dòng. Từ md
                trở lên mới đủ chỗ để xếp ngang. */}
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between md:gap-4">
                <div className="flex min-w-0 items-start gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <div className="min-w-0">
                        <h1 className="text-2xl font-bold tracking-tight">
                            {mode === 'edit' ? m.grower_edit() : m.grower_new()}
                        </h1>
                        <p className="mt-1 text-sm text-muted-foreground">
                            {m.grower_page_subtitle()}
                        </p>
                    </div>
                </div>
                {/* justify-end: trên điện thoại hàng nút nằm dưới tiêu đề và
                    căn sang phải, để nút Lưu — thứ hay bấm nhất — rơi đúng
                    dưới ngón cái thuận. */}
                <div className="flex shrink-0 justify-end gap-2.5">
                    {/* Cả hai nút icon dưới đây chỉ có nghĩa khi hộ đã tồn tại:
                        màn tạo mới chưa có số điện thoại để mã hóa QR, cũng
                        chưa có hộ nào để gắn khuôn mặt vào. */}
                    {mode === 'edit' && growerId && (
                        <Button
                            asChild
                            type="button"
                            variant="outline"
                            size="icon"
                            title={m.face_card_title()}
                            aria-label={m.face_card_title()}
                        >
                            <Link
                                to="/agriculture/growers/$id/faces"
                                params={{ id: growerId }}
                            >
                                <ScanFace className="size-4" />
                            </Link>
                        </Button>
                    )}
                    {/* QR vùng trồng đọc vùng từ BẢN ĐÃ LƯU (`initial`), không
                        lấy từ form: trang công khai chỉ tồn tại cho vùng server
                        đã cấp id, nên QR của một vùng vừa gõ trên màn sẽ trỏ vào
                        hư không. */}
                    {mode === 'edit' && (
                        <GrowerAreaQRTrigger
                            areas={initial?.areas}
                            growerName={initial?.name}
                            // SĐT cũng lấy từ bản ĐÃ LƯU như vùng, không lấy từ
                            // form: mã QR phải khớp dữ liệu hộ đang có trên
                            // server, không khớp số vừa gõ mà chưa lưu.
                            growerPhone={initial?.phone}
                            loading={!initial}
                            entry="grower_edit"
                        />
                    )}
                    {mode === 'edit' && (
                        <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => {
                                // Chụp giá trị lúc mở bằng getValues chứ không
                                // watch: watch bắt cả trang render lại theo từng
                                // ký tự gõ vào ô số điện thoại.
                                setQrPhone(form.getValues('phone') ?? '')
                                setQrOpen(true)
                            }}
                            title={m.grower_qr_button()}
                            aria-label={m.grower_qr_button()}
                        >
                            <QrCode className="size-4" />
                        </Button>
                    )}
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate({ to: '/agriculture/growers' })}
                    >
                        {m.action_cancel()}
                    </Button>
                    {!readOnly && (
                        <Button type="submit" disabled={submitting}>
                            {submitting ? '...' : m.grower_save_button()}
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid gap-5 lg:grid-cols-2 lg:items-start">
                <FormSection icon={<User />} title={m.grower_general_section()}>
                    <GrowerGeneralSection form={form} readOnly={readOnly} />
                </FormSection>

                <FormSection
                    icon={<FileText />}
                    title={m.grower_profile_section()}
                >
                    <GrowerProfileSection
                        form={form}
                        readOnly={readOnly}
                        attachments={attachments}
                    />
                </FormSection>
            </div>

            <FormSection icon={<MapPinned />} title={m.grower_areas()}>
                <GoogleMapsProvider>
                    <GrowerAreasSection
                        control={form.control}
                        readOnly={readOnly}
                        landDrafts={landDrafts}
                        growerId={mode === 'edit' ? growerId : undefined}
                        setAreaGeo={(i: number, geo: AreaGeo) => {
                            form.setValue(`areas.${i}.paths`, geo.paths, {
                                shouldDirty: true,
                            })
                            form.setValue(`areas.${i}.markers`, geo.markers, {
                                shouldDirty: true,
                            })
                        }}
                    />
                </GoogleMapsProvider>
            </FormSection>

            {/* Số lấy từ form, không lấy từ response: nhân viên sửa số rồi
                bấm QR phải ra đúng con số đang hiện trên màn. */}
            <GrowerQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                growerName={initial?.name}
                phone={qrPhone}
                loading={mode === 'edit' && !initial}
            />
        </form>
    )
}

/**
 * Ghép từng vùng trên form với id vùng thật trong bản ghi hộ vừa lưu.
 *
 * Ghép theo VỊ TRÍ là đúng và là cách duy nhất: server giữ nguyên thứ tự mảng
 * `areas` gửi lên (nó chỉ điền id/mã cho vùng còn thiếu), nên vùng thứ i trong
 * form chính là vùng thứ i trong response. Vùng vừa thêm tới đây mới có id —
 * đó cũng là lý do giấy tờ đất phải đợi lưu xong mới tải lên được.
 */
function areaTargets(
    values: GrowerFormValues,
    saved: Grower | undefined,
): AreaDraftTarget[] {
    return values.areas.map((a, i) => ({
        draftKey: a.draftKey ?? '',
        areaId: a.id ?? saved?.areas?.[i]?.id,
    }))
}
